import { useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES, SHARED_ROUTES } from "definitions/constants/routeConstants";
import { pathKeys } from "features/Path/hooks";
import { emailKeys } from "features/Resources/Email/hooks";
import { messageKeys } from "features/Resources/Message/hooks";
import { sharedResourceKeys } from "features/Resources/Shared/hooks";
import { taskKeys } from "features/Resources/Task/hooks";
import { userKeys } from "features/User/hooks";
import useCaminoStore from "hooks/useCaminoStore";
import { User } from "features/User/models";
import { generatePath, useNavigate } from "react-router";
import apiClient from "services/ApiClient";

export const adminKeys = {
  all: ["admin"] as const,
  company: () => [...adminKeys.all, "companies"] as const,
  emailConfiguration: () => [...adminKeys.company(), "emailConfiguration"] as const,
  users: () => [...adminKeys.company(), "users"] as const,
};

export function useImpersonateUser(user: User) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const setValue = useCaminoStore((state) => state.setValue);
  return useMutation({
    mutationFn: () => apiClient.post<User>(generatePath(API_ROUTES.admin.users.impersonations.show, { userId: user?.id || "" }), {}),
    onSuccess: (resp) => {
      const { company, ...restUser } = resp;
      queryClient.clear();
      setValue({
        currentUser: restUser,
        currentCompany: company,
        currentRole: restUser.permission[0],
        isImpersonating: true,
      });
      navigate(SHARED_ROUTES.home);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });
}

export function useDeleteUser(user: User) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => apiClient.delete(generatePath(API_ROUTES.admin.users.show, { id: user?.id || "" })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast({ title: "User deleted", status: "success" });
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });
}

export function useDeleteAutomations(user: User) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => apiClient.delete(generatePath(API_ROUTES.admin.users.automations.base, { userId: user?.id || "" })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sharedResourceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: emailKeys.all });
      queryClient.invalidateQueries({ queryKey: messageKeys.all });
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: pathKeys.lists() });
      toast({ title: "Automations deleted", status: "success" });
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });
}

export function useValidateAutomations(user: User) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => apiClient.post(generatePath(API_ROUTES.admin.users.automations.validate, { userId: user.id || "" }), {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sharedResourceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: emailKeys.all });
      queryClient.invalidateQueries({ queryKey: messageKeys.all });
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: pathKeys.lists() });
      toast({ title: "Automations validated", status: "success" });
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });
}
